/**
 * validateForm — schema-aware whole-form validation.
 *
 * Returns a map of field → error message for every required field that is
 * missing a value. Section-level progress can be derived from the same pass.
 *
 * Only validates fields whose appears_when expression evaluates to true.
 * This mirrors react-hook-form's per-field validation but gives us a
 * centralised "is this form submittable?" check before calling the backend.
 *
 * Visibility is delegated to the shared `isVisible` helper in
 * `schema-renderer/expression/visibility.ts` so the renderer (ConditionalR)
 * and this validator cannot disagree about whether a field is visible —
 * a hidden field is never required (e.g. the "I don't know" parent gate
 * on the 47SP family-entry repeaters: ticking the parent boolean hides the
 * children in the renderer, and the validator must respect the same gate
 * or the user gets a blocking "this field is required" error pointing at
 * a field they can't see).
 */

import type { FormSchema, SectionDef, SubsectionDef, FieldDef } from '../schema-renderer/types';
import type { FormState } from './formStateAdapter';
import { readNestedPath } from './formStateAdapter';
import { analyseAddressHistoryCoverage, formatGaps } from './addressHistoryCoverage';
import { isVisible, withEntryContext } from '../schema-renderer/expression/visibility.js';
import { isValidDateForType } from '../schema-renderer/validation/calendarDate.js';

/** Map of canonical_name → error message. Empty = valid. */
export type ValidationErrors = Record<string, string>;

/** Per-section progress summary. */
export interface SectionProgress {
  section_id: string;
  title: string;
  /** All required visible fields are filled. */
  complete: boolean;
  /** At least one field in this section has any value. */
  touched: boolean;
  /** Count of required visible fields missing a value. */
  missingRequired: number;
  /** Count of required visible fields total. */
  totalRequired: number;
}

/**
 * Run full-form validation and return per-field errors plus per-section progress.
 *
 * @param schema   - The FormSchema for the current form
 * @param state    - Current flat form values (canonical_name → value)
 */
export function validateForm(
  schema: FormSchema,
  state: FormState,
): { errors: ValidationErrors; sections: SectionProgress[] } {
  const errors: ValidationErrors = {};
  const sections: SectionProgress[] = [];

  for (const section of schema.sections) {
    const sectionProgress = validateSection(section, state, errors);
    sections.push(sectionProgress);
  }

  applyFormLevelRules(schema, state, errors);

  return { errors, sections };
}

/**
 * Cross-field BLOCKING rules that span a whole repeater, applied after the
 * per-field walk. Runs in every validateForm caller, so the client submit
 * gate and the server-side stage latch can never disagree.
 *
 * Address-history completeness (Tzvi ruling 2026-07-19, partner forms): the
 * Department needs a complete 10-year address history, so the
 * countries-resided ranges must tile the window with no gaps before the form
 * can be finished. The matching live warning is in crossFieldWarnings.
 */
const ADDRESS_HISTORY_FORMS = new Set([
  '47SP_820_onshore_stage1',
  '47SP_309_offshore_stage1',
  '47SP_300_PMV',
]);

/**
 * Forms that carry the `application.stream` choice (600 visitor family). The
 * Family Sponsored stream cannot be lodged from within Australia (item 2a):
 * Immi routes an in-Australia application to the Tourist stream. Per-option
 * gating is not expressible in the schema, so this is a form-level blocking
 * rule with plain-English copy, the same shape as the address-history rule.
 */
const STREAM_ONSHORE_FORMS = new Set(['1419_tourist', '1418_family_sponsored']);

function applyFormLevelRules(schema: FormSchema, state: FormState, errors: ValidationErrors): void {
  applyAddressHistoryRule(schema, state, errors);
  applyOnshoreStreamRule(schema, state, errors);
}

function applyAddressHistoryRule(schema: FormSchema, state: FormState, errors: ValidationErrors): void {
  if (!ADDRESS_HISTORY_FORMS.has(schema.form_id)) return;
  const hasLived = readNestedPath(state, 'applicant.countries_resided.has_lived_outside_citizenship');
  if (hasLived !== 'Yes') return;
  const entries = readNestedPath(state, 'applicant.countries_resided.entries');
  const coverage = analyseAddressHistoryCoverage(entries);
  if (!coverage.complete) {
    errors['applicant.countries_resided.entries'] =
      `You have entered a gap and there is no address history for that gap. Please fill in the gap. Missing periods: ${formatGaps(coverage.gaps)}.`;
  }
}

function applyOnshoreStreamRule(schema: FormSchema, state: FormState, errors: ValidationErrors): void {
  if (!STREAM_ONSHORE_FORMS.has(schema.form_id)) return;
  // Read flat-or-nested (state may be a flat canonical-name map or the RHF tree).
  const outsideAustralia = readValue(state, 'applicant.is_outside_australia', undefined);
  const stream = readValue(state, 'application.stream', undefined);
  if (outsideAustralia === 'No' && stream === 'Family Sponsored') {
    errors['application.stream'] =
      'Applications for the Visitor visa made within Australia are for the Tourist stream of the visa. Select the Tourist stream, or record the applicant as being outside Australia.';
  }
}

function validateSection(
  section: SectionDef,
  state: FormState,
  errors: ValidationErrors,
): SectionProgress {
  // Check section-level appears_when first
  if (!isVisible(section.appears_when, state)) {
    return {
      section_id: section.section_id,
      title: section.title,
      complete: true,
      touched: false,
      missingRequired: 0,
      totalRequired: 0,
    };
  }

  const counters: SectionCounters = { totalRequired: 0, missingRequired: 0, touched: false };

  // Top-level fields
  for (const field of section.fields ?? []) {
    walkField(field, state, errors, counters);
  }

  // Subsections
  for (const sub of section.subsections ?? []) {
    walkSubsection(sub, state, errors, counters);
  }

  return {
    section_id: section.section_id,
    title: section.title,
    complete: counters.missingRequired === 0 && counters.totalRequired > 0,
    touched: counters.touched,
    missingRequired: counters.missingRequired,
    totalRequired: counters.totalRequired,
  };
}

function walkSubsection(
  sub: SubsectionDef,
  state: FormState,
  errors: ValidationErrors,
  counters: SectionCounters,
): void {
  if (!isVisible(sub.appears_when, state)) return;

  for (const field of sub.fields) {
    walkField(field, state, errors, counters);
  }
}

interface SectionCounters {
  totalRequired: number;
  missingRequired: number;
  touched: boolean;
}

/**
 * Recursively walk a field. Handles repeaters by iterating their entries
 * with an entry-scoped state so wildcarded `appears_when` expressions
 * resolve against the specific entry being validated.
 */
function walkField(
  field: FieldDef,
  state: FormState,
  errors: ValidationErrors,
  counters: SectionCounters,
  errorKey?: string,
): void {
  // Field-level visibility — covers both top-level fields and repeater
  // entry fields (where state has been pre-scoped by withEntryContext).
  const condition = field.appears_when ?? field.conditional_on;
  if (!isVisible(condition, state)) return;

  if (field.type === 'repeater') {
    walkRepeater(field, state, errors, counters);
    return;
  }

  // Leaf field — required check.
  const lookupKey = errorKey ?? field.canonical_name;
  const value = readValue(state, field.canonical_name, errorKey);
  const hasSomeValue = !isEmpty(value);
  const isRequired = field.required === true;

  if (isRequired) counters.totalRequired += 1;
  if (hasSomeValue) counters.touched = true;

  if (isRequired && !hasSomeValue) {
    const label = field.labels['default'] ?? field.canonical_name;
    errors[lookupKey] = `${label} is required`;
    counters.missingRequired += 1;
    return;
  }

  // Calendar-date validity: a filled date field must be a real date. The shape
  // pattern (fromSchema) accepts impossible dates like 1990-13-45; the submit
  // gate must reject them so an invalid DOB can't be lodged.
  if (hasSomeValue && (field.type === 'date' || field.type === 'date_partial')) {
    const raw = typeof value === 'string' ? value : String(value);
    if (!isValidDateForType(raw, field.type)) {
      const label = field.labels['default'] ?? field.canonical_name;
      errors[lookupKey] = `${label} must be a valid date`;
      if (isRequired) counters.missingRequired += 1;
      return;
    }

    // Cross-field date rule (item 20): this date must be strictly AFTER the
    // value of another field (e.g. departure after arrival). validation_overrides
    // is single-field, so the comparison lives here where the referenced field's
    // value is in scope. Uses the SAME (possibly entry-scoped) state, so a rule
    // between two fields of the same repeater entry resolves correctly.
    const afterField = field.validation_overrides?.after_field;
    if (afterField && !errors[lookupKey]) {
      const otherRaw = readValue(state, afterField, undefined);
      const other = typeof otherRaw === 'string' ? otherRaw : otherRaw == null ? '' : String(otherRaw);
      // Only compare when the referenced field is itself a filled, valid date;
      // if it is empty or malformed, its own required/calendar checks report it.
      if (other.trim() !== '' && isValidDateForType(other, field.type) && !isAfter(raw, other)) {
        const label = field.labels['default'] ?? field.canonical_name;
        errors[lookupKey] =
          field.validation_overrides?.after_field_message ?? `${label} must be after the earlier date`;
        if (isRequired) counters.missingRequired += 1;
      }
    }
  }
}

/**
 * True when ISO date string `a` is strictly after ISO date string `b`.
 * Accepts full dates (yyyy-mm-dd) and partials (yyyy / yyyy-mm), normalising a
 * bare year to Jan 1 the same way the live date_range validator does, so the
 * submit gate and the renderer agree.
 */
function isAfter(a: string, b: string): boolean {
  const parse = (v: string): number => new Date(v.length === 4 ? `${v}-01-01` : v).getTime();
  const da = parse(a);
  const db = parse(b);
  if (Number.isNaN(da) || Number.isNaN(db)) return true; // format handled elsewhere
  return da > db;
}

function walkRepeater(
  field: FieldDef,
  state: FormState,
  errors: ValidationErrors,
  counters: SectionCounters,
): void {
  const repeaterPath = field.canonical_name;
  const entries = readArray(state, repeaterPath);

  // Surface the repeater's own min_count requirement as a required-style
  // error if the user hasn't added enough entries.
  const minCount = field.min_count ?? 0;
  if (minCount > 0) {
    counters.totalRequired += 1;
    if (entries.length < minCount) {
      const label = field.labels['default'] ?? field.canonical_name;
      errors[repeaterPath] = `${label} requires at least ${minCount} entr${minCount === 1 ? 'y' : 'ies'}`;
      counters.missingRequired += 1;
    } else {
      counters.touched = true;
    }
  } else if (entries.length > 0) {
    counters.touched = true;
  }

  // Walk each entry's schema with a state that resolves wildcards to this
  // specific entry. The entry-scoped state lets `appears_when` expressions
  // like `entries[*].information_unknown != "Yes"` resolve against the
  // entry being validated, so hidden fields are correctly skipped.
  const entrySchema = field.entry_schema ?? [];
  for (let i = 0; i < entries.length; i++) {
    const scoped = withEntryContext(state, repeaterPath, i);
    for (const child of entrySchema) {
      const childKey = `${repeaterPath}.${i}.${tail(child.canonical_name)}`;
      walkField(child, scoped, errors, counters, childKey);
    }
  }
}

/**
 * Read the value for a canonical_name from form state. The state may be
 * flat (key = canonical_name) or nested (RHF default). For repeater entry
 * children we accept an explicit override key.
 */
function readValue(
  state: FormState,
  canonicalName: string,
  overrideKey: string | undefined,
): unknown {
  // Override key is used for repeater entry fields, e.g.
  // "applicant.foo.entries.0.family_name". Try that first.
  if (overrideKey) {
    if (Object.prototype.hasOwnProperty.call(state, overrideKey)) {
      return state[overrideKey];
    }
    // Walk the nested object using the override key (RHF default shape).
    const walked = walkNested(state, overrideKey);
    if (walked !== undefined) return walked;
  }

  // Direct flat lookup on canonical name.
  if (Object.prototype.hasOwnProperty.call(state, canonicalName)) {
    return state[canonicalName];
  }
  return walkNested(state, canonicalName);
}

function walkNested(state: FormState, path: string): unknown {
  const segments = path.split('.').filter(Boolean);
  let current: unknown = state;
  for (const seg of segments) {
    if (current === null || current === undefined) return undefined;
    if (Array.isArray(current)) {
      const idx = Number(seg);
      if (Number.isInteger(idx)) {
        current = current[idx];
        continue;
      }
      return undefined;
    }
    if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[seg];
      continue;
    }
    return undefined;
  }
  return current;
}

function readArray(state: FormState, path: string): unknown[] {
  const direct = state[path];
  if (Array.isArray(direct)) return direct;
  const walked = walkNested(state, path);
  return Array.isArray(walked) ? walked : [];
}

/** Last dot-segment of a canonical name: `a.b.c[*].family_name` -> `family_name` */
function tail(canonicalName: string): string {
  const stripped = canonicalName.replace(/\[\*\]/g, '');
  const idx = stripped.lastIndexOf('.');
  return idx === -1 ? stripped : stripped.slice(idx + 1);
}

function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).every(isEmpty);
  return false;
}

/**
 * Compute a quick section-progress indicator without collecting errors.
 * Used by the sidebar to show ✅ / 🟡 / ⚪ without a full validation pass.
 */
export function computeSectionProgress(
  schema: FormSchema,
  state: FormState,
): SectionProgress[] {
  const { sections } = validateForm(schema, state);
  return sections;
}

/**
 * Strip everything the client must never see: client_hidden SECTIONS, and
 * client_hidden SUBSECTIONS inside otherwise-visible sections.
 *
 * The subsection pass matters because the agent-managed blocks (ADI's
 * authorised-recipient and correspondence-channel details, seeded at form
 * creation) live inside client-facing sections on the 47SP forms. Hiding the
 * whole section would take the client's residential address with it.
 *
 * A section whose subsections are ALL staff-only, and which has no top-level
 * fields, is dropped entirely — otherwise it renders as an empty step with a nav
 * badge counting zero fields.
 *
 * This is the ONE place the rule lives. IntakeFormV2's render filter, the
 * server-side stage latch (via stageCompletion's re-export) and
 * computeOwnFieldProgress all call it, so the client's submit gate, the
 * server's re-verification and the progress % can never disagree about what
 * the client was asked. (Defined here rather than stageCompletion.ts because
 * stageCompletion imports this module — the reverse import would be a cycle.)
 */
export function stripClientHidden(sections: SectionDef[]): SectionDef[] {
  const visible: SectionDef[] = [];
  for (const section of sections) {
    if (section.client_hidden) continue;
    const subsections = (section.subsections ?? [])
      .filter((sub) => !sub.client_hidden)
      .map(stripHiddenFieldsFromSubsection);
    const fields = stripHiddenFields(section.fields);
    if ((section.subsections?.length ?? 0) > 0 && subsections.length === 0 && !fields?.length) continue;
    const unchanged =
      subsections.length === (section.subsections?.length ?? 0) &&
      subsections.every((sub, i) => sub === section.subsections![i]) &&
      fields === section.fields;
    visible.push(unchanged ? section : { ...section, subsections, ...(fields !== section.fields ? { fields } : {}) });
  }
  return visible;
}

/** Drop client_hidden fields (and client_hidden entry_schema fields inside repeaters). */
function stripHiddenFields(fields: FieldDef[] | undefined): FieldDef[] | undefined {
  if (!fields) return fields;
  let changed = false;
  const out: FieldDef[] = [];
  for (const f of fields) {
    if (f.client_hidden) {
      changed = true;
      continue;
    }
    const entries = (f as { entry_schema?: FieldDef[] }).entry_schema;
    if (entries?.some((e) => e.client_hidden)) {
      changed = true;
      out.push({ ...f, entry_schema: entries.filter((e) => !e.client_hidden) } as FieldDef);
    } else {
      out.push(f);
    }
  }
  return changed ? out : fields;
}

function stripHiddenFieldsFromSubsection(sub: SubsectionDef): SubsectionDef {
  const fields = stripHiddenFields(sub.fields);
  return fields === sub.fields ? sub : { ...sub, fields: fields ?? [] };
}

/**
 * Indices of sections whose section-level appears_when currently evaluates
 * hidden for the given form state.
 *
 * Exists for the intake nav (review fix 2026-07-22): SectionRenderer already
 * blanks a gated section's body via ConditionalR, and validation/progress
 * skip it via isVisible, but the section list used for the nav and Next/Prev
 * traversal never evaluated section-level gates, so a gated section (e.g.
 * relationship_registration on a Married matter) stayed a reachable, blank
 * nav entry. The nav filters on this set, using the SAME isVisible rule as
 * validation so the two can never disagree.
 */
export function hiddenSectionIndices(sections: SectionDef[], state: FormState): Set<number> {
  const out = new Set<number>();
  sections.forEach((section, i) => {
    if (!isVisible(section.appears_when, state)) out.add(i);
  });
  return out;
}

/** A single validation error tagged with which section it belongs to. */
export interface ScopedError {
  key: string;
  message: string;
}

/** Sentinel bucket for errors we could not map to any section (e.g. the
 *  submit-level `_latch` failure, or a stray form-level key). The caller shows
 *  these on the current section so they are never silently swallowed. */
export const UNSCOPED_ERRORS = '_unscoped';

/**
 * Bucket a flat ValidationErrors map by the section each error belongs to
 * (Jane 2026-07-21 ruling: validation errors render inside their own
 * subsection or tab only, never as one global banner across sections).
 *
 * Returns a Map keyed by section_id (plus the UNSCOPED_ERRORS bucket), so the
 * intake nav can badge the sections that have problems and the section body can
 * show only its own errors.
 *
 * Error keys are canonical_names, or repeater-entry keys like
 * `applicant.foo.entries.0.family_name`. The latter are normalised back to the
 * schema's wildcard form (`applicant.foo.entries[*].family_name`) so they match
 * the field's declared canonical_name.
 */
export function groupErrorsBySection(
  schema: FormSchema,
  errors: ValidationErrors,
): Map<string, ScopedError[]> {
  const canonicalToSection = new Map<string, string>();
  const register = (field: FieldDef, sectionId: string): void => {
    if (field.canonical_name) canonicalToSection.set(field.canonical_name, sectionId);
    const entries = (field as { entry_schema?: FieldDef[] }).entry_schema;
    if (entries) for (const child of entries) register(child, sectionId);
  };
  for (const section of schema.sections) {
    for (const field of section.fields ?? []) register(field, section.section_id);
    for (const sub of section.subsections ?? []) {
      for (const field of sub.fields) register(field, section.section_id);
    }
  }

  const out = new Map<string, ScopedError[]>();
  const push = (sectionId: string, err: ScopedError): void => {
    const list = out.get(sectionId) ?? [];
    list.push(err);
    out.set(sectionId, list);
  };

  for (const [key, message] of Object.entries(errors)) {
    const normalised = key.replace(/\.\d+(?=\.|$)/g, '[*]');
    let sectionId =
      canonicalToSection.get(key) ??
      canonicalToSection.get(normalised) ??
      // Trailing entry index with no child field (repeater min_count on
      // `a.entries.0`): fall back to the repeater's own canonical.
      canonicalToSection.get(normalised.replace(/\[\*\]$/, ''));
    if (!sectionId) sectionId = UNSCOPED_ERRORS;
    push(sectionId, { key, message });
  }

  return out;
}

/**
 * Display-only completion metric, shared by the intake-form header AND the
 * client dashboard so the two percentages can never disagree.
 *
 * Unlike computeSectionProgress (section-granular, over the MERGED state, drives
 * the submit gate + section badges), this is FIELD-granular and counts ONLY the
 * current user's OWN required, visible fields. Cross-form fields owned by another
 * form (owner_form ≠ this form_id) are read-only context prefilled from a related
 * form — the user can't author them — so they are excluded entirely and neither
 * inflate nor cap the client's %. Because cross-form context only ever fills
 * foreign-owned keys, a form's own-field values are identical whether computed
 * over the live merged state (intake header) or the saved rows (dashboard), so
 * both surfaces return the same number.
 *
 * Returns a 0-100 integer. Empty form → 0; all own required filled → 100. A
 * form with no own required fields returns 0 (nothing to complete yet).
 *
 * client_hidden sections and subsections are EXCLUDED: they are agent-managed
 * blocks the client never sees (ADI's recipient/contact/assistance prefills),
 * so they must neither inflate the % on day one (they arrive pre-answered) nor
 * make 100% unreachable. This makes the number "of what the client can see,
 * how much is done" on every surface, including the staff view of the header.
 */
export function computeOwnFieldProgress(schema: FormSchema, state: FormState): number {
  const counters: SectionCounters = { totalRequired: 0, missingRequired: 0, touched: false };
  const throwaway: ValidationErrors = {};
  const isForeign = (f: FieldDef) => f.owner_form != null && f.owner_form !== schema.form_id;

  for (const section of stripClientHidden(schema.sections)) {
    if (!isVisible(section.appears_when, state)) continue;
    for (const field of section.fields ?? []) {
      if (isForeign(field)) continue;
      walkField(field, state, throwaway, counters);
    }
    for (const sub of section.subsections ?? []) {
      if (!isVisible(sub.appears_when, state)) continue;
      for (const field of sub.fields) {
        if (isForeign(field)) continue;
        walkField(field, state, throwaway, counters);
      }
    }
  }

  if (counters.totalRequired === 0) return 0;
  return Math.round(
    ((counters.totalRequired - counters.missingRequired) / counters.totalRequired) * 100,
  );
}

/**
 * A section is "context-only" for a given form when every one of its fields is
 * a cross-form shared field owned by another form (owner_form ≠ this form_id) —
 * i.e. read-only verification data prefilled from a related form that the
 * current user cannot author. Such sections (e.g. the 40SP sponsor form's
 * "Applicant — Identity (verification)" block) must not count toward the
 * client's own completion %, and their missing-required hints are meaningless
 * because the client can't fill them. A section with no fields is not
 * context-only.
 */
export function isContextOnlySection(section: SectionDef, formId: string): boolean {
  const fields: FieldDef[] = [
    ...(section.fields ?? []),
    ...(section.subsections ?? []).flatMap((s) => s.fields ?? []),
  ];
  if (fields.length === 0) return false;
  return fields.every((f) => f.owner_form != null && f.owner_form !== formId);
}
